import { Box, Grid, styled } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { routeName } from 'routes/routes-name'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** API */
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import { getBranch, getSecureItem } from 'utils/app.utils'

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import { useDownloadExcel } from 'component/Excel/hooks/useExcel'
import InputSecondNewDatePicker, { PICKER_VIEWS } from 'component/Input/InputSecondNewDatePicker'
import Loading from 'component/Loading'
import RemittanceList from './remittancesList'

/** STYLE */
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { resetModal, showModal } from 'app/slice/modal.slice'
import { notiError } from 'component/notifications/notifications'
import { KEY_STORAGE } from 'constants/common'
import { colors } from 'constants/theme'
import CheckboxButton from 'new-components/buttons/checkbox-button'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import PageModal from 'new-components/modal/page-modal'
import { useDispatch } from 'react-redux'
import ExportToExcel from './ExportToExcel'

const SelectorProcess = styled(Box)(({ theme }) => ({
  '.css-16wllh2-MuiButtonBase-root-MuiButton-root.btn-style': {
    color: `${colors.disabledGray} !important`,
    border: '1px solid rgba(0, 0, 0, 0.12)!important'
  },
  '.btn-pop-process': {
    maxHeight: '32px',
    width: ' 100% !Important',
    maxWidth: '180px',
    display: 'flex !Important',
    justifyContent: 'space-between !Important',
    alignItems: 'center !Important',

    '.icon-pop': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'black'
    }
  },
  '.pop-process-open': {
    opacity: '1 !important',
    display: 'block !important'
  },
  '.pop-process-close': {
    opacity: '0',
    display: 'none'
  },
  '.pop-process-container': {
    transition: 'all 0.2s ',
    position: 'fixed',
    border: '1px solid #E5E5E5',
    backgroundColor: '#fff',
    padding: '6px 10px',
    '.pop-content': {
      padding: '8px 0px',
      fontSize: '14px',
      '.pop-content-main': {
        display: 'flex ',
        justifyContent: 'space-between ',
        alignItems: 'center '
      }
    }
  }
}))

export type SubSelectorProcessProps = {
  name: string
  title: string
  isShow?: boolean | undefined
  isProcess?: boolean | undefined
  type: 'topic' | 'sub'
}

export type SelectorProcessProps = {
  DF: SubSelectorProcessProps
  LAB: SubSelectorProcessProps
  TREATMENTRIGHT: SubSelectorProcessProps
  CASHSLIP: SubSelectorProcessProps
  INCOME_EXPENSES: SubSelectorProcessProps
  SUMMARY_TREATMENT_TYPE: SubSelectorProcessProps
}

export default function Remittances() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)
  const momentNow = moment().format('YYYY-MM-DD')

  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [rowCount, setRowCount] = useState<number>(0)
  const [sortBy, setSortBy] = useState<string>('createAt')
  const [sortType, setSortType] = useState<string>('ASC')
  const [loading, setLoading] = useState<boolean>(false)

  const [remittanceData, getRemittanceData] = useState<any[]>([])
  const [remittanceDate, setRemittanceDate] = useState<string>(momentNow)
  const [remittancePage, setRemittancePage] = useState<number>(1)
  const [remittanceLimitPage, setRemittanceLimitPage] = useState<number>(10)

  const [checkDF, setCheckDf] = useState<boolean>(false)
  const [checkCash, setCheckCash] = useState<boolean>(false)

  const [checkTreatmentRight, setCheckTreatmentRight] = useState<boolean>(false)
  const [popSettingDisplay, setPopSettingDisplay] = useState<boolean>(false)
  const branchInfo = getSecureItem(KEY_STORAGE.CLINIC_INFO)
  const [dataProcess, setDataProcess] = useState<SelectorProcessProps>({
    "DF": {
      title: t('REPORT.MODAL_DISPLAY.DF'),
      isShow: false,
      isProcess: false,
      type: 'topic',
      name: 'DF'
    },
    "LAB": {
      title: t('REPORT.MODAL_DISPLAY.LAB'),
      isShow: false,
      isProcess: undefined,
      type: 'sub',
      name: 'LAB'
    },
    "TREATMENTRIGHT": {
      title: t('REPORT.MODAL_DISPLAY.TREATMENTRIGHT'),
      isShow: false,
      isProcess: false,
      type: 'topic',
      name: 'TREATMENTRIGHT'
    },
    "CASHSLIP": {
      title: t('REPORT.MODAL_DISPLAY.CASHSLIP'),
      isShow: undefined,
      isProcess: false,
      type: 'topic',
      name: "CASHSLIP"
    },
    "INCOME_EXPENSES": {
      title: t('REPORT.MODAL_DISPLAY.INCOME_EXPENSES'),
      isShow: false,
      isProcess: false,
      type: 'topic',
      name: "INCOME_EXPENSES"
    },
    "SUMMARY_TREATMENT_TYPE": {
      title: t('REPORT.MODAL_DISPLAY.SUMMARY_TREATMENT_TYPE'),
      isShow: false,
      isProcess: undefined,
      type: 'topic',
      name: "SUMMARY_TREATMENT_TYPE"
    }
  })

  const exportExcel = async () => {
    setLoading(true);
    try {
      await ExportToExcel({
        data: remittanceData,
        branches,
        branchId,
        filter: {},
        filterDate: remittanceDate,
        dataProcess,
        branchInfo,
        t,
      })
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      setLoading(false);
    }
  }

  const onOpenSentingDisplay = () => {
    setPopSettingDisplay(true)
    dispatch(showModal())
  }

  const onCloseSentingDisplay = () => {
    dispatch(resetModal())
    setPopSettingDisplay(false)
  }

  const verifyPermission = async () => {
    const condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 200
    }

    ReportApi.reportList(condition).then(({ data }) => {
      const check = _.find(data, (val: any) => String(val.reportKey) === 'REMITTANCES') ? true : false
      if (!check) {
        history.push(routeName.report)
        return false
      }
    })

    ClinicApi.findAllBranches()
      .then(({ data }) => setBranches(data))
      .catch((e) => {
        return
      })

    return true
  }

  useEffect(() => {
    verifyPermission()
  }, [])


  const fetchRemittance = async (pageArg: number) => {
    setLoading(true)
    try {
      let condition: any = {
        page: remittancePage,
        pageLimit: remittanceLimitPage,
        branchId: branchId,
        date: remittanceDate
      }
      if (sortType) condition = { ...condition, sortType: sortType }
      if (sortBy) condition = { ...condition, sortBy: sortBy }
      if (checkDF) condition = { ...condition, df: checkDF === true ? 1 : 0 }
      if (checkTreatmentRight) condition = { ...condition, expenses: checkTreatmentRight === true ? 1 : 0 }
      if (dataProcess) condition = { ...condition, lab: dataProcess?.LAB.isShow === true ? 1 : 0 }
      if (remittanceDate !== null) condition = { ...condition, date: remittanceDate }
      const { headers, data } = await ReportApi.reportRemittances(condition)
      getRemittanceData(data)
      setRowCount(Number(headers['x-total'] || 0))
    } catch (err) {
      notiError(t('ERROR'))
      setLoading(false)
    }
    finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRemittance(remittancePage)
  }, [remittancePage, remittanceLimitPage, sortBy, sortType])

  const filter = [branchId === 0 ? `${t(`REPORT.FILTER.ALL`)}` : `${_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')}`, remittanceDate]

  const { onDownload } = useDownloadExcel({
    tableExportRef: componentRef.current,
    filename: `${t(`REPORT.REPORT_NAME.REMITTANCE_NEW`)} (${remittanceDate})`,
    sheetName: `${t(`REPORT.REPORT_NAME.REMITTANCE_NEW`)}`
  })

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangeRowsPerPage = (limit: number) => setRemittanceLimitPage(limit)

  const handleChangePage = (val: number) => setRemittancePage(val)

  const reportPermissions = () => {
    ReportApi.reportPermissions(getBranch(), 30)
      .then(({ data }) => {
        if (data?.permission) {
          setDataProcess(data.permission[0])
        }
      })
      .catch((e) => {
        return
      })
  }

  useEffect(() => {
    reportPermissions()
  }, [])

  const handleChangeSettingDisplay = async (data: SelectorProcessProps) => {
    await ReportApi.reportPermissionsUpdate(getBranch(), { reportId: 30, permission: [data] })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          setDataProcess(data)
        }
      })
      .catch((e) => {
        return
      })
  }


  return (
    <Box>
      <HeaderCard text={t(`REPORT.REPORT_NAME.REMITTANCE_NEW`)} />
      <Box px={3} pt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <SelectInput
              label={t('REPORT.FILTER.BRANCH')}
              value={branchId}
              onChange={e => setBranchId(Number(e.target.value))}
              options={[
                ...branches.map(b => ({ value: b.branchId, name: b.branchName || b.branchNameEn }))
              ]}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InputSecondNewDatePicker
              dateFormat={'DD/MM/YYYY'}
              placeholder={t('REPORT.FILTER.DATE')}
              label={''}
              inputHeight={40}
              value={remittanceDate}
              onchange={(e: any) => {
                setRemittanceDate(moment(e).format('YYYY-MM-DD'))
                setRemittancePage(1)
              }}
              views={PICKER_VIEWS.DAY || PICKER_VIEWS.MONTH || PICKER_VIEWS.YEAR}
            />
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" flexDirection="row" alignItems="center" gap="16px">
              <MainButton
                title={t('REPORT.MODAL_DISPLAY.BUTTON_SETTING_DISPLAY')}
                type="primary"
                variant="outlined"
                onClick={onOpenSentingDisplay}
                endIcon={<MoreVertIcon />}
              />
              <MainButton
                title={t('INPUT.SEARCH')}
                type="primary"
                variant="contained"
                style={{ width: '120px' }}
                onClick={fetchRemittance}
              />
            </Box>
            <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
              if (e === 'Excel') {
                exportExcel();
              } else if (e === 'PDF') {
                const style = document.createElement("style");
                style.innerHTML = `
                      @media print {
                        @page { size: portrait }
                      }
                    `;
                document.head.appendChild(style);
                window.print();
                document.head.removeChild(style);

              }
            }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
          </Grid>
        </Grid>
        <Box marginTop="16px">
          <RemittanceList
            page={remittancePage}
            pageLimit={remittanceLimitPage}
            rowCount={rowCount}
            sortBy={sortBy}
            sortType={sortType}
            data={remittanceData}
            onRequestSort={onRequestSort}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleChangePage={handleChangePage}
            componentRef={componentRef}
            dataProcess={dataProcess}
            branchInfo={branchInfo}
            filter={filter}
          />
        </Box>
      </Box>
      <Loading show={loading} type="fullLoading" />
      {popSettingDisplay &&
        <ModalSettingDisplay
          onClose={onCloseSentingDisplay}
          dataProcess={dataProcess}
          handleChangeSettingDisplay={handleChangeSettingDisplay}
        />
      }
    </Box>
  )
}

type ModalSettingDisplayProps = {
  onClose: () => void
  dataProcess: SelectorProcessProps
  handleChangeSettingDisplay: (data: SelectorProcessProps) => void
}

const ModalSettingDisplay = (props: ModalSettingDisplayProps) => {
  const { t } = useTranslation()
  const { onClose, dataProcess, handleChangeSettingDisplay } = props

  const [infoProcess, setInfoProcess] = useState<SelectorProcessProps>(dataProcess)
  const keyPass = [1, 3, 5]

  const onHandleChangeSettingDisplay = (elementName: 'isShow' | 'isProcess', index: number) => {
    const newDataProcess = Object.values(infoProcess)
    if (elementName === 'isShow' && !keyPass.includes(index)) {
      const newIsProcess = !newDataProcess[index][elementName] ? {} : { isProcess: false }
      newDataProcess[index] = { ...newDataProcess[index], [elementName]: !newDataProcess[index][elementName], ...newIsProcess }
    } else {
      newDataProcess[index] = { ...newDataProcess[index], [elementName]: !newDataProcess[index][elementName] }
    }
    setInfoProcess({ ...infoProcess, [newDataProcess[index].name]: { ...newDataProcess[index] } })
  }

  const renderFormSettingDisplay = (data: SubSelectorProcessProps, index: number) => {
    return (
      <Grid container item xs={12} key={index}>
        <Grid item xs={6} >
          <Box sx={{ marginLeft: data.type !== 'topic' ? '1rem' : '' }}>
            {`${data.type !== 'topic' ? '-' : ''}${t(`REPORT.MODAL_DISPLAY.${data.name}`)}`}
          </Box>
        </Grid>
        <Grid item xs={3} sx={{ '.MuiButtonBase-root': { paddingLeft: '0 !important' } }}>
          {data?.isShow !== undefined &&
            <CheckboxButton
              checked={data.isShow}
              onChange={() => onHandleChangeSettingDisplay('isShow', index)}
            />
          }
        </Grid>
        <Grid item xs={3} sx={{ '.MuiButtonBase-root': { paddingLeft: '0 !important' } }}>
          {data?.isProcess !== undefined &&
            <CheckboxButton
              checked={data?.isProcess}
              onChange={() => onHandleChangeSettingDisplay('isProcess', index)}
              disabled={!keyPass.includes(index) && !data.isShow}
            />
          }
        </Grid>
      </Grid>
    )
  }

  return (
    <PageModal
      isOpen={true}
      title={t('REPORT.MODAL_DISPLAY.TITLE')}
      size='S'
      handleClose={() => onClose()}
      onSubmit={{
        onClick: () => {
          handleChangeSettingDisplay(infoProcess)
          onClose()
        }, title: t('REPORT.MODAL_DISPLAY.BUTTON_SUBMIT')
      }}
      onCancel={{ onClick: () => onClose(), title: t('BUTTON.CANCEL') }}
    >
      <Box>
        <Grid container>
          <Grid container item xs={12}>
            <Grid item xs={6} />
            <Grid item xs={3} >
              {t('REPORT.MODAL_DISPLAY.ISSHOW')}
            </Grid>
            <Grid item xs={3} >
              {t('REPORT.MODAL_DISPLAY.ISPROCESS')}
            </Grid>
          </Grid>
          {infoProcess && Object.values(infoProcess).map((item, index) => {
            return (renderFormSettingDisplay(item, index))
          })}
        </Grid>
      </Box>
    </PageModal>
  )
}